import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useStrings } from '../../state/useLocale';
import type { ToastFn } from '../../App';
import { EmojiPicker } from './EmojiPicker';

/**
 * Everything you can do with one message.
 *
 * React, reply, pin, copy a link to it, report it, block whoever sent it —
 * and, on your own, edit it or delete it. Behind one button rather than as a
 * row on every message: a message list at 280px has room for the message and
 * nothing else, and a row of affordances that appears on hover is a row of
 * affordances a keyboard never finds.
 *
 * The menu is therefore two menus that share a trigger, and which one you get
 * is decided by whose message it is. Four items are everybody's. Report and
 * Block exist only on somebody else's, because "tell someone about this" and
 * "stop seeing this person" are not questions you can ask about yourself.
 * Edit and Delete are the exact mirror: they exist only on your own, for the
 * same reason and in the same place, set apart below the four with the same
 * rule above them. Nobody is ever shown an action that belongs to the other
 * half — this is the one thing in the menu that is hidden rather than
 * disabled-with-a-reason, because a disabled Delete on a stranger's message
 * would be the panel explaining that you cannot delete other people's
 * messages, which nobody was wondering.
 *
 * Which is the reason this is a real button in the document rather than a
 * hover target. The stylesheet may fade it until the row is hovered or
 * focused — that is a stylesheet's business — but it is in the tab order on
 * every row, it opens on Enter, and the menu it opens is walked with the arrow
 * keys and shut with Escape. This is a workstation. Everything in it has a
 * key.
 *
 * The menu opens straight into the reaction palette when it was opened by the
 * `+` at the end of a reaction row, and into the list of actions otherwise.
 * Reacting is the cheap act this whole feature exists to make cheap — the
 * server gives reactions their own limiter for the same reason — and making it
 * two decisions deep would have priced it back out.
 *
 * The palette is the composer's own `EmojiPicker`, not a shortlist of six
 * faces somebody chose. There is already one palette in this panel, it already
 * has search and groups and the fifty-eight emoji this product drew for
 * itself at the front of it, and a second one would be a second opinion about
 * what a reaction is. `cleanReaction` accepts exactly what that picker offers,
 * so anything reachable in it is something the room will take.
 *
 * Two actions are shown and refused rather than hidden. Pinning is for
 * signed-in people and a room that is full of pins cannot take another one;
 * in both cases the button is disabled with the reason on it, because a
 * control that vanishes teaches nobody why it was there. Blocking and
 * reporting are hidden on your own messages, which is different: those are not
 * refusals, they are questions that do not exist.
 *
 * Copying the link is the one action that can fail for a reason that has
 * nothing to do with the chat. `navigator.clipboard` is absent over plain HTTP
 * and rejects in a window that is not focused, so the failure is expected
 * rather than exceptional: the menu then hands over the link in a field,
 * selected, and says which keys copy it — the same fallback the progress-code
 * dialog has used since the day it was written.
 */
export interface MessageMenuProps {
  /** The message this menu belongs to. */
  id: number;
  /** True on your own message: nothing to report and nobody to block. */
  own: boolean;
  /** True while this menu is the open one. */
  open: boolean;
  /** True when it was opened by the `+` on a reaction row. */
  reacting: boolean;
  /** Whether this message is pinned right now. */
  pinned: boolean;
  /** Whether the sender is blocked in this browser right now. */
  blocked: boolean;
  /** Historical display labels cannot identify a person to block. */
  canBlock?: boolean;
  /** A listener may read a room but not act in it. */
  canPost: boolean;
  /** Pinning is a verified member's; the room refuses anyone else. */
  canPin: boolean;
  /** The room is already holding as many pins as it may. */
  pinsFull: boolean;
  /**
   * Whether this message can be given new words: yours, something you typed
   * rather than something the world announced, and a composer to type them
   * in. A listener owns its old messages and can delete them, and has nowhere
   * to write — so the two are not the same question and are not one flag.
   */
  canEdit: boolean;
  /** The permalink, from lib/chat's `chatLink`. */
  link: string;
  onOpen(reacting: boolean): void;
  onClose(): void;
  onReact(emoji: string): void;
  onReply(): void;
  onPin(on: boolean): void;
  onEdit(): void;
  onDelete(): void;
  onReport(): void;
  onBlock(on: boolean): void;
  notify: ToastFn;
}

export function MessageMenu({
  id,
  own,
  open,
  reacting,
  pinned,
  blocked,
  canBlock = true,
  canPost,
  canPin,
  pinsFull,
  canEdit,
  link,
  onOpen,
  onClose,
  onReact,
  onReply,
  onPin,
  onEdit,
  onDelete,
  onReport,
  onBlock,
  notify,
}: MessageMenuProps) {
  const s = useStrings().chat;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const linkRef = useRef<HTMLInputElement>(null);
  // Set when the clipboard refused. Not an error state: the link is handed
  // over instead, and the keyboard finishes the job.
  const [manual, setManual] = useState(false);
  // True while the menu opens above its trigger instead of below it. Measured,
  // never guessed — see the layout effect below.
  const [up, setUp] = useState(false);

  // The first item takes focus when the menu opens with the keyboard or the
  // pointer alike, so Down-Down-Enter and click-click behave the same way.
  useEffect(() => {
    if (!open || reacting) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus();
  }, [open, reacting]);

  useEffect(() => {
    if (manual) linkRef.current?.select();
  }, [manual]);

  // A menu that is closed remembers nothing about the last time it was open.
  useEffect(() => {
    if (!open) setManual(false);
  }, [open]);

  /**
   * Open upwards when there is not room to open downwards.
   *
   * The conversation is a scrolling box (`.chat-list` is `overflow-y: auto`),
   * so a menu on the last visible message is not merely awkward — it is
   * *clipped*, and the items past the fold cannot be reached by pointer at
   * all. The last message is also the one most likely to be acted on, so this
   * is the common case rather than the corner.
   *
   * Measured against the list rather than the window, because the list is
   * what does the clipping. The second half of the test matters as much as
   * the first: in a panel at its 240px floor neither direction fits, and
   * flipping into an even smaller gap would trade a clipped menu for a worse
   * clipped menu. So it only flips when above is genuinely roomier.
   *
   * `useLayoutEffect`, not `useEffect`, because this runs in the same commit
   * that puts the menu on screen and the alternative is one frame of the menu
   * visibly in the wrong place. `reacting` is a dependency because swapping
   * the items for the emoji palette changes the height this is all about.
   *
   * Flipping is not always enough, and the second half of this is what covers
   * that. At the panel's 280×240 floor (`CHAT_MIN`) the list is shorter than
   * the six items, so neither direction fits and choosing the roomier one only
   * decides which end gets cut off. So the menu is also told how much room it
   * actually has, and gives itself a scrollbar when it is more than that:
   * every item stays reachable by pointer and by arrow key, which is the whole
   * requirement. Written as a custom property rather than a `max-height`
   * because the `@` completion wears this same class and sets its own
   * (chat.css), and the stylesheet should stay the one place that decides.
   * Cleared before measuring, so the measurement is always of the menu at its
   * full height rather than of the clamp left over from the last time.
   */
  useLayoutEffect(() => {
    if (!open) {
      setUp(false);
      return;
    }
    const menu = menuRef.current;
    const trigger = triggerRef.current;
    const list = trigger?.closest('.chat-list');
    if (!menu || !trigger || !list) return;
    menu.style.removeProperty('--menu-room');
    const bounds = list.getBoundingClientRect();
    const seat = trigger.getBoundingClientRect();
    const below = bounds.bottom - seat.bottom;
    const above = seat.top - bounds.top;
    const tall = menu.offsetHeight;
    const flip = tall > below && above > below;
    setUp(flip);
    const room = Math.max(flip ? above : below, 0);
    if (tall > room) menu.style.setProperty('--menu-room', `${Math.round(room)}px`);
  }, [open, reacting, manual]);

  const shut = () => {
    onClose();
    triggerRef.current?.focus();
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      // Ours before the panel's: one Escape shuts the menu, a second shuts
      // the chat.
      e.preventDefault();
      e.stopPropagation();
      shut();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
    const items = [...(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? [])];
    if (items.length === 0) return;
    e.preventDefault();
    const at = items.indexOf(document.activeElement as HTMLElement);
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? items.length - 1
          : // Wrapping, because a six-item menu is a ring and stopping dead at
            // the end of it is a keystroke wasted on nothing.
            (at + (e.key === 'ArrowDown' ? 1 : items.length - 1) + items.length) % items.length;
    items[next]?.focus();
  };

  const copy = async () => {
    try {
      // Absent over plain HTTP, and rejected outright by some browsers when
      // the document is not focused. Neither is worth an apology: the link is
      // handed over selected instead.
      if (!navigator.clipboard?.writeText) throw new Error('no clipboard');
      await navigator.clipboard.writeText(link);
      notify(s.menu.copied);
      shut();
    } catch {
      setManual(true);
    }
  };

  const act = (run: () => void) => () => {
    run();
    shut();
  };

  return (
    <div className="chat-menu-holder">
      <button
        ref={triggerRef}
        type="button"
        className="msg-menu-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={`chat-menu-${id}`}
        aria-label={s.menu.open}
        title={s.menu.open}
        onClick={() => (open ? onClose() : onOpen(false))}
      >
        <svg viewBox="0 0 12 12" aria-hidden="true" width="11" height="11">
          <circle cx="2" cy="6" r="1" fill="currentColor" />
          <circle cx="6" cy="6" r="1" fill="currentColor" />
          <circle cx="10" cy="6" r="1" fill="currentColor" />
        </svg>
      </button>

      {open && (
        <div
          ref={menuRef}
          id={`chat-menu-${id}`}
          className={up ? 'chat-menu is-up' : 'chat-menu'}
          role="menu"
          aria-label={s.menu.label}
          onKeyDown={onKeyDown}
        >
          {reacting ? (
            <EmojiPicker
              recent={[]}
              onPick={(emoji) => {
                onReact(emoji);
                shut();
              }}
              onClose={shut}
            />
          ) : manual ? (
            /* The clipboard said no. The link is right here instead. */
            <div className="chat-menu-manual">
              <input
                ref={linkRef}
                className="chat-input"
                type="text"
                readOnly
                value={link}
                aria-label={s.menu.copyLabel}
                // A URL is machine text and stays left to right in both
                // languages (docs/i18n.md §7).
                dir="ltr"
              />
              <p className="note">{s.menu.copyManual}</p>
            </div>
          ) : (
            <>
              <button
                type="button"
                role="menuitem"
                className="chat-menu-item"
                disabled={!canPost}
                onClick={() => onOpen(true)}
              >
                {s.menu.react}
              </button>
              <button type="button" role="menuitem" className="chat-menu-item" disabled={!canPost} onClick={act(onReply)}>
                {s.menu.reply}
              </button>
              <button
                type="button"
                role="menuitem"
                className="chat-menu-item"
                disabled={!canPin || (pinsFull && !pinned)}
                title={!canPin ? s.menu.pinGated : pinsFull && !pinned ? s.menu.pinFull : s.menu.pinWhy}
                onClick={act(() => onPin(!pinned))}
              >
                {pinned ? s.menu.unpin : s.menu.pin}
              </button>
              <button type="button" role="menuitem" className="chat-menu-item" onClick={copy}>
                {s.menu.copy}
              </button>
              {/* Your own two, set apart below the four with the rule
                  `is-grave` draws — the same rule that sets off Report and
                  Block on somebody else's message, because these sit in the
                  same place and are the same kind of thing: the items that
                  change something rather than add to it.

                  Edit is shown and refused rather than hidden when there is
                  nowhere to type, which is the panel's usual way round: a
                  listener who came back to a room they once posted in should
                  be told why the item is dark, not left to wonder where it
                  went. Delete beside it is never refused — taking something
                  back is the one thing you can always do about your own
                  message, whatever the room now thinks you are. */}
              {own && (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="chat-menu-item is-grave"
                    disabled={!canEdit}
                    title={s.menu.editWhy}
                    onClick={act(onEdit)}
                  >
                    {s.menu.edit}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="chat-menu-item is-grave"
                    title={s.menu.deleteWhy}
                    onClick={act(onDelete)}
                  >
                    {s.menu.delete}
                  </button>
                </>
              )}
              {/* The two that act on a person rather than on a message, set
                  apart below the others: `is-grave` is the stylesheet's rule
                  for an item you should not reach by accident. */}
              {!own && (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="chat-menu-item is-grave"
                    disabled={!canPost}
                    onClick={act(onReport)}
                  >
                    {s.menu.report}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="chat-menu-item is-grave"
                    title={s.menu.blockWhy}
                    disabled={!canBlock}
                    onClick={act(() => onBlock(!blocked))}
                  >
                    {blocked ? s.menu.unblock : s.menu.block}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default MessageMenu;
